import { useMemo, useState } from "react";
import MovementModal from "@/pages/admin/inventory/components/MovementModal";
import TransferModal from "@/pages/admin/inventory/components/TransferModal";
import { useInventoryData } from "@/hooks/useInventory";
import { useAuth } from "@/context/AuthContext";

const currency = new Intl.NumberFormat("es-DO", {
  style: "currency",
  currency: "DOP",
  minimumFractionDigits: 2,
});

const MOVEMENT_META: Record<string, { label: string; icon: string; tone: string }> = {
  entrada: { label: "Entrada", icon: "ri-arrow-down-line", tone: "bg-accent-100 text-accent-900" },
  salida: { label: "Salida", icon: "ri-arrow-up-line", tone: "bg-primary-100 text-primary-700" },
  ajuste: { label: "Ajuste", icon: "ri-equalizer-line", tone: "bg-secondary-200 text-secondary-900" },
  inicial: { label: "Inicial", icon: "ri-flag-line", tone: "bg-secondary-100 text-secondary-900" },
  correccion: { label: "Corrección", icon: "ri-edit-2-line", tone: "bg-background-200 text-foreground-700" },
  transferencia: { label: "Transferencia", icon: "ri-swap-box-line", tone: "bg-background-200 text-foreground-700" },
};

function formatDate(value: string): string {
  const date = new Date(value);
  return date.toLocaleString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminInventoryPage() {
  const { profile, hasPermission } = useAuth();
  const canSeeCost = hasPermission("cost.view");
  const { lines, movements, products, branches, loading, error, reload } = useInventoryData();

  const [tab, setTab] = useState<"stock" | "movements">("stock");
  const [branchFilter, setBranchFilter] = useState(profile?.branch_id ?? "");
  const [search, setSearch] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [movementOpen, setMovementOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);

  const filteredLines = useMemo(() => {
    const term = search.trim().toLowerCase();
    return lines.filter((line) => {
      if (branchFilter && line.branch_id !== branchFilter) return false;
      if (onlyLow && !line.low && !line.out) return false;
      if (!term) return true;
      return `${line.product_name} ${line.product_sku ?? ""}`.toLowerCase().includes(term);
    });
  }, [lines, branchFilter, search, onlyLow]);

  const stats = useMemo(() => {
    const scoped = lines.filter((line) => !branchFilter || line.branch_id === branchFilter);
    const low = scoped.filter((line) => line.low || line.out).length;
    const out = scoped.filter((line) => line.out).length;
    const value = scoped.reduce((sum, line) => sum + line.stock * Number(line.cost_price ?? 0), 0);
    return { rows: scoped.length, low, out, value };
  }, [lines, branchFilter]);

  const filteredMovements = useMemo(() => {
    const term = search.trim().toLowerCase();
    return movements.filter((movement) => {
      if (branchFilter && movement.branch_id !== branchFilter) return false;
      if (!term) return true;
      return `${movement.product_name} ${movement.reason ?? ""} ${movement.reference ?? ""}`
        .toLowerCase()
        .includes(term);
    });
  }, [movements, branchFilter, search]);

  const statCards = [
    { key: "rows", label: "Registros de stock", value: String(stats.rows), icon: "ri-stack-line", tone: "primary" },
    { key: "low", label: "Bajo stock", value: String(stats.low), icon: "ri-alert-line", tone: "accent" },
    { key: "out", label: "Agotados", value: String(stats.out), icon: "ri-close-circle-line", tone: "secondary" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Inventario</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Stock por sucursal con movimientos auditables. Cada cambio queda registrado con usuario,
            sucursal y fecha.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTransferOpen(true)}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-swap-box-line text-base"></i>
            Transferir
          </button>
          <button
            type="button"
            onClick={() => setMovementOpen(true)}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-base"></i>
            Movimiento
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">{card.value}</p>
              <p className="mt-1 text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
        {canSeeCost ? (
          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-money-dollar-circle-line text-base"></i>
            </span>
            <div>
              <p className="font-heading text-sm font-bold leading-none text-foreground-950">
                {currency.format(stats.value)}
              </p>
              <p className="mt-1 text-[11px] text-foreground-500">Valor a costo</p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex w-full items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1 lg:w-auto">
          <button
            type="button"
            onClick={() => setTab("stock")}
            className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors lg:flex-none ${
              tab === "stock" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:bg-background-200"
            }`}
          >
            Stock
          </button>
          <button
            type="button"
            onClick={() => setTab("movements")}
            className={`flex-1 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors lg:flex-none ${
              tab === "movements"
                ? "bg-primary-500 text-background-50"
                : "text-foreground-600 hover:bg-background-200"
            }`}
          >
            Movimientos
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 sm:w-64">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar producto..."
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <select
            value={branchFilter}
            onChange={(event) => setBranchFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todas las sucursales</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          {tab === "stock" ? (
            <button
              type="button"
              onClick={() => setOnlyLow((prev) => !prev)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
                onlyLow
                  ? "border-primary-300 bg-primary-50 text-primary-700"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className="ri-alert-line text-base"></i>
              Solo alertas
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando inventario...
        </div>
      ) : tab === "stock" ? (
        filteredLines.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-stack-line text-2xl"></i>
            </span>
            <p className="text-sm font-semibold text-foreground-800">Sin registros de stock</p>
            <p className="max-w-sm text-xs text-foreground-500">
              Crea productos para que aparezcan aquí, o registra un movimiento de inventario inicial.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-background-200">
            <div className="hidden lg:block">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3">Sucursal</th>
                    <th className="px-4 py-3 text-right">Stock</th>
                    <th className="px-4 py-3 text-right">Mínimo</th>
                    <th className="px-4 py-3">Unidad</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200 bg-background-50">
                  {filteredLines.map((line) => (
                    <tr key={line.id} className="text-sm text-foreground-800">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
                            {line.product_image ? (
                              <img src={line.product_image} alt={line.product_name} className="h-full w-full object-cover" />
                            ) : (
                              <i className="ri-image-line text-foreground-400"></i>
                            )}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-foreground-950">{line.product_name}</p>
                            <p className="truncate text-[11px] text-foreground-400">{line.product_sku ?? "Sin SKU"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-600">{line.branch_name}</td>
                      <td className="px-4 py-3 text-right font-heading font-bold text-foreground-950">{line.stock}</td>
                      <td className="px-4 py-3 text-right text-foreground-600">{line.min_stock}</td>
                      <td className="px-4 py-3 text-xs text-foreground-600">{line.unit ?? "—"}</td>
                      <td className="px-4 py-3">
                        {line.out ? (
                          <span className="inline-flex rounded-full bg-primary-100 px-2.5 py-0.5 text-[10px] font-semibold text-primary-700">
                            Agotado
                          </span>
                        ) : line.low ? (
                          <span className="inline-flex rounded-full bg-accent-100 px-2.5 py-0.5 text-[10px] font-semibold text-accent-900">
                            Bajo stock
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full bg-background-200 px-2.5 py-0.5 text-[10px] font-semibold text-foreground-700">
                            Disponible
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setMovementOpen(true)}
                          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                        >
                          <i className="ri-add-line text-sm"></i>
                          Movimiento
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:hidden">
              {filteredLines.map((line) => (
                <div key={line.id} className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-50">
                      {line.product_image ? (
                        <img src={line.product_image} alt={line.product_name} className="h-full w-full object-cover" />
                      ) : (
                        <i className="ri-image-line text-lg text-foreground-400"></i>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground-950">{line.product_name}</p>
                      <p className="truncate text-[11px] text-foreground-400">{line.branch_name}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold text-secondary-900">
                          {line.stock} {line.unit ?? "u"}
                        </span>
                        {line.out ? (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                            Agotado
                          </span>
                        ) : line.low ? (
                          <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold text-accent-900">
                            Bajo stock
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : filteredMovements.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-history-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin movimientos</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Registra entradas, salidas, ajustes o transferencias para ver el historial aquí.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filteredMovements.map((movement) => {
            const meta = MOVEMENT_META[movement.movement_type] ?? MOVEMENT_META.ajuste;
            const positive = movement.quantity >= 0;
            return (
              <div
                key={movement.id}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${meta.tone}`}>
                    <i className={`${meta.icon} text-base`}></i>
                  </span>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-foreground-950">{movement.product_name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.tone}`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                      {movement.branch_name} · {movement.reason || movement.reference || "Sin motivo"} ·{" "}
                      {formatDate(movement.created_at)}
                    </p>
                    <p className="truncate text-[11px] text-foreground-400">Usuario: {movement.user_name}</p>
                  </div>
                </div>
                <span
                  className={`shrink-0 font-heading text-base font-bold ${
                    positive ? "text-accent-700" : "text-primary-700"
                  }`}
                >
                  {positive ? "+" : ""}
                  {movement.quantity}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <MovementModal
        open={movementOpen}
        onClose={() => setMovementOpen(false)}
        onSaved={reload}
        products={products}
        branches={branches}
        defaultBranchId={profile?.branch_id ?? null}
      />

      <TransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onSaved={reload}
        products={products}
        branches={branches}
        defaultBranchId={profile?.branch_id ?? null}
      />
    </div>
  );
}